import * as React from 'react';

import SuperCritical from 'lib/supercritical';
import { noDefault } from 'lib/eventWrappers';
import { tzmoment, tzmomentMod } from 'lib/tzmoment';
import TZDate from 'lib/TZDate';

import {
    Movements,
    Movement,
    MovementDetails,
    TrafficCoordinationCard,
} from './movements';

import { CCardStatusIndicator } from 'portcall/ccardstatusindicator/view';
import { PCModal } from 'portcall/pcmodal/view';
import { PortCallScheduleView } from 'portcall/portcallschedule/view';

import { LoadingIcon } from './loadingicon';
import { PCDismissible } from 'portcall/pcdismissible/view';

export const MovementsView = SuperCritical(({ model } : { model : Movements }) =>
    <div className="homepage-movements">
        <LoadingIcon loading = {model.Loading} />
        <MovementDetailsPopup model={model} />
        <ul>
            {model.DisplayedPorts().map(p =>
                <li key={p.PortId()}>
                    <label className="port-name">{p.PortName()}</label>
                    <ul>
                        {p.Movements().map(m =>
                            <li
                                key={m.Model.FromPointId()} 
                                className={"movement" + (m.HasBegun() ? " has-begun" : "")}
                                onClick={m.ShowDetails}
                            >
                                <div className="movement-row">
                                    <label className="time">{m.MovementStartTime() && tzmoment(m.MovementStartTime()!).format('HH:mm')}</label>
                                    <label className="movement">{m.Model.Type()}</label>
                                    <label className="vessel-name">{m.Model.VesselName()}</label>
                                    <span className="location">
                                        <label className="from-name">{m.Model.FromPoint()?.Location()?.Name()}</label>
                                        <span className="to-label pc-text-smalllabel" hidden={!m.Model.ToPoint()}>
                                            {' '}
                                            to
                                            {' '}
                                        </span>
                                        <label className="to-name" hidden={!m.Model.ToPoint()}>{m.Model.ToPoint()?.Location()?.Name()}</label>
                                    </span>
                                    {!m.HasBegun() && <TrafficCCards movement={m} />}
                                    <MovementCompleteTime movement={m} model={model} />
                                    {/* Adding a column for movements with no PJO or mid-PJO; TBD: showing ccards during a PJO */}
                                    {m.HasBegun() && !m.MovementCompleteTime() && <span className="last-column"></span>}
                                    <span className="screen-size-phone-lg-spacer"></span>
                                </div>
                            </li>
                        )}
                    </ul>
                </li>
            )}
        </ul>
    </div>
);

const MovementDetailsPopup = SuperCritical(({ model } : { model : Movements }) => {
    const details = model.MovementDetails();
    return (
        <PCModal className="movement-details-modal" shown={() => !!details} onBackDropClick={details?.Close}>
            <div className="modal-content">
                <div className="modal-header">
                    <a href="" onClick={details ? noDefault(details.Close) : undefined}>&lt;&lt; Back</a>
                    <span className="details-actions-shell">
                        <a
                            href=""
                            className="details-actions-menu-btn fa fa-bars"
                            onClick={details ? noDefault(details.ToggleActionsMenu) : undefined}
                        />
                        {details && <MovementDetailsActionsMenu details={details} />}
                    </span>
                </div>
                <div className="modal-body">
                    {details && <PortCallScheduleView model={details.Schedule} />}
                </div>
            </div>
        </PCModal>
    );
});

const MovementDetailsActionsMenu = SuperCritical(({ details } : { details : MovementDetails }) => {
    if (details.ShowActionsMenu()) {
        const
            m = details.Movement,
            res = m.FromPoint()?.BerthReservation() ?? m.ToPoint()?.BerthReservation() ?? null,
            pt = res?.Itinerary() ?? null;
        return (
            <PCDismissible
                className="pc-actions-menu pc-card details-actions-menu"
                onDismiss={() => details.ShowActionsMenu(false)}
            >
                {res && pt &&
                    <div className="action-item">
                        <a
                            href={`#!schedulegrid?date=${
                                tzmoment(pt.Arrival.Effective())
                                    .add(-1, 'days')
                                    .format('YYYY-MM-DD')
                            }&resid=${res.Id()}`}
                        >
                            View on Berths
                        </a>
                    </div>
                }
                <div className="action-item">
                    <a href={`#!portcallprofile?id=${m.PortCallId()}`}>View PortCall</a>
                </div>
            </PCDismissible>
        );
    } else {
        return null;
    }
});

const TrafficCCards = SuperCritical(({ movement: m } : { movement : Movement }) =>
    <div className="coordination-cards last-column">
        {m.TrafficCoordinationCards().map(ccard =>
            <TrafficCCard ccard={ccard} key={ccard.Model.Id()} />
        )}
    </div>
);

const TrafficCCard = SuperCritical(({ ccard } : { ccard : TrafficCoordinationCard }) =>
    <div className="coordination-card">
        <CCardStatusIndicator statusId={ccard.Model.StatusId()} />
        <span className="title">{ccard.Model.Coordination()?.Title()}</span>
        <span className="status-label">({ccard.Model.EffectiveStatusLabel() ?? 'loading...'})</span>
    </div>
);

const MovementCompleteTime = SuperCritical(({ movement: m , model } : { movement : Movement, model : Movements }) => {
    const completionDateTime = m.MovementCompleteTime();
    if (completionDateTime === null) return null;

    const
        completionDate = tzmomentMod(model.TZUtils, completionDateTime, d => d.startOf('day')),
        dataDate = tzmomentMod(model.TZUtils, model.Date(), d => d.startOf('day')),
        isSameDay = TZDate.eq(completionDate, dataDate);
    return (
        <label className="mooring last-column">
            {m.Model.Type() === 'out' ? "cleared at" : 
                m.Model.Type() === 'area' ? "arrived at" : "moored at"}
            <span className="mooring-time">{tzmoment(completionDateTime).format(isSameDay ? 'HH:mm' : 'HH:mm, MMM DD')}</span>
        </label>
    );
});



// WEBPACK FOOTER //
// ./apps/portcall/homepage/movementsview.tsx